import React, { useState, useEffect } from 'react';
import './AdminInstallPrompt.css';

const AdminInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Détecter iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Détecter si l'app est déjà installée (mode standalone)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone ||
                       document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Vérifier si l'app est déjà installée
    if (standalone) {
      setIsInstalled(true);
      return;
    }

    // Écouter l'événement beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Afficher le prompt après 5 secondes pour ne pas être intrusif
      setTimeout(() => {
        // Vérifier si l'utilisateur n'a pas déjà rejeté
        const dismissed = localStorage.getItem('admin-pwa-install-dismissed');
        if (!dismissed) {
          setShowPrompt(true);
        }
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Écouter les changements de statut réseau
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Afficher le prompt d'installation
    deferredPrompt.prompt();

    // Attendre la réponse de l'utilisateur
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
      setShowPrompt(false);
      localStorage.removeItem('admin-pwa-install-dismissed');
    } else {
      // L'utilisateur a rejeté, ne plus afficher pendant 7 jours
      localStorage.setItem('admin-pwa-install-dismissed', Date.now().toString());
    }

    // Réinitialiser le prompt
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Ne plus afficher pendant 7 jours
    localStorage.setItem('admin-pwa-install-dismissed', Date.now().toString());
  };

  // Ne pas afficher si déjà installée ou si l'utilisateur a rejeté récemment
  if (isInstalled || isStandalone || !showPrompt) {
    // Vérifier si le rejet est ancien (plus de 7 jours)
    const dismissed = localStorage.getItem('admin-pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      if (dismissedTime > sevenDaysAgo) {
        return null;
      } else {
        // Le rejet est ancien, on peut réafficher
        localStorage.removeItem('admin-pwa-install-dismissed');
      }
    } else {
      return null;
    }
  }

  // Instructions pour iOS
  if (isIOS && !isStandalone) {
    return (
      <div className="admin-install-prompt ios-prompt">
        <div className="admin-install-prompt-content">
          <div className="admin-install-prompt-icon">📱</div>
          <h3>Installer l'application Admin</h3>
          <p>Pour installer cette application sur votre iPhone/iPad :</p>
          <ol>
            <li>Appuyez sur le bouton <strong>Partager</strong> <span className="share-icon">⎋</span></li>
            <li>Sélectionnez <strong>"Sur l'écran d'accueil"</strong></li>
            <li>Appuyez sur <strong>"Ajouter"</strong></li>
          </ol>
          <div className="admin-install-prompt-actions">
            <button className="admin-install-dismiss-btn" onClick={handleDismiss}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Prompt pour Android/Chrome
  if (deferredPrompt && !isIOS) {
    return (
      <div className="admin-install-prompt android-prompt">
        <div className="admin-install-prompt-content">
          <div className="admin-install-prompt-icon">📲</div>
          <h3>Installer l'application Admin</h3>
          <p>Installez cette application pour un accès rapide à l'interface administrateur depuis votre mobile.</p>
          <div className="admin-install-benefits">
            <div className="benefit-item">✓ Accès rapide</div>
            <div className="benefit-item">✓ Mode hors ligne</div>
            <div className="benefit-item">✓ Notifications</div>
          </div>
          <div className="admin-install-prompt-actions">
            <button className="admin-install-btn" onClick={handleInstallClick}>
              Installer maintenant
            </button>
            <button className="admin-install-dismiss-btn" onClick={handleDismiss}>
              Plus tard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AdminInstallPrompt;



